import { useCallback, useEffect, useState, useRef } from 'react';
import { MonacoWrapper } from './MonacoWrapper';
import { AlertCircle, CheckCircle2, Copy, Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { editor } from 'monaco-editor';

export interface CodeError {
  line: number;
  column: number;
  message: string;
  severity: 'error' | 'warning';
}

interface CodeViewProps {
  value: string;
  onChange: (value: string) => void;
  onValidate?: (errors: CodeError[]) => void;
  isPrimitive?: boolean;
  className?: string;
}

export function CodeView({
  value,
  onChange,
  onValidate,
  isPrimitive = false,
  className = '',
}: CodeViewProps) {
  const [errors, setErrors] = useState<CodeError[]>([]);
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert Monaco markers to our CodeError format
  const handleMonacoValidation = useCallback((markers: editor.IMarkerData[]) => {
    const codeErrors: CodeError[] = markers.map((marker) => ({
      line: marker.startLineNumber,
      column: marker.startColumn,
      message: marker.message,
      severity: marker.severity === 8 ? 'error' : 'warning', // 8 = Error in Monaco
    }));
    setErrors(codeErrors);
    if (onValidate) {
      onValidate(codeErrors);
    }
  }, [onValidate]);

  // Handle content change
  const handleChange = useCallback((newValue: string | undefined) => {
    if (newValue !== undefined) {
      onChange(newValue);
    }
  }, [onChange]);

  // Copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [value]);

  // Download as JSON file
  const handleDownload = useCallback(() => {
    try {
      const parsed = JSON.parse(value);
      const filename = parsed.name
        ? `${parsed.name.toLowerCase().replace(/\s+/g, '-')}.json`
        : `flow-${Date.now()}.json`;

      const blob = new Blob([value], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // If JSON is invalid, just use generic filename
      const blob = new Blob([value], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `flow-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [value]);

  // Import from file
  const handleImport = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      try {
        // Validate JSON
        JSON.parse(content);
        onChange(content);
      } catch (err) {
        console.error('Invalid JSON file:', err);
      }
    };
    reader.readAsText(file);

    // Reset input so same file can be imported again
    e.target.value = '';
  }, [onChange]);

  const hasErrors = errors.some(e => e.severity === 'error');
  const hasWarnings = errors.some(e => e.severity === 'warning');

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {isPrimitive ? 'Primitive JSON' : 'Flow JSON'}
          </span>
          {hasErrors ? (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-destructive/10 text-destructive text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.filter(e => e.severity === 'error').length} error(s)</span>
            </div>
          ) : hasWarnings ? (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-yellow-500/10 text-yellow-600 dark:text-yellow-500 text-xs">
              <AlertCircle className="w-3 h-3" />
              <span>{errors.filter(e => e.severity === 'warning').length} warning(s)</span>
            </div>
          ) : value.trim() && (
            <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/10 text-green-600 dark:text-green-500 text-xs">
              <CheckCircle2 className="w-3 h-3" />
              <span>Valid JSON</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCopy}
            title="Copy to clipboard"
            className="h-7 px-2"
          >
            <Copy className="w-3.5 h-3.5 mr-1.5" />
            <span className="text-xs">{copied ? 'Copied!' : 'Copy'}</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDownload}
            title="Download as JSON file"
            className="h-7 px-2"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" />
            <span className="text-xs">Export</span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleImport}
            title="Import from JSON file"
            className="h-7 px-2"
          >
            <Upload className="w-3.5 h-3.5 mr-1.5" />
            <span className="text-xs">Import</span>
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Monaco Editor */}
      <div className="flex-1 min-h-0">
        <MonacoWrapper
          value={value}
          onChange={handleChange}
          onValidate={handleMonacoValidation}
        />
      </div>

      {/* Error Panel (shown when there are errors) */}
      {errors.length > 0 && (
        <div className="border-t border-border bg-card max-h-32 overflow-y-auto">
          <div className="px-4 py-2">
            <h4 className="text-xs font-medium text-muted-foreground mb-2">
              Issues ({errors.length})
            </h4>
            <div className="space-y-1">
              {errors.slice(0, 5).map((error, index) => (
                <div
                  key={index}
                  className={cn(
                    'flex items-start gap-2 text-xs p-1.5 rounded',
                    error.severity === 'error'
                      ? 'bg-destructive/10 text-destructive'
                      : 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-500'
                  )}
                >
                  <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                  <span>
                    <span className="font-medium">Line {error.line}:</span> {error.message}
                  </span>
                </div>
              ))}
              {errors.length > 5 && (
                <div className="text-xs text-muted-foreground pl-5">
                  ...and {errors.length - 5} more
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
